"use client";

import React from "react";
import { cn } from "utils";
import { useShellRouter } from "@/src/hooks";
import NextImage from "../../NextImage";
import type { ChatStructuredAsset, ChatStructuredInsightBullet } from "./types";

import { ChatCardEyebrow } from "./common/ChatCardEyebrow";
import { ChatCardIntro } from "./common/ChatCardIntro";
import { ChatStructuredCardShell } from "./common/ChatStructuredCardShell";

export const ManagementBulletIcon = () => (
  <svg
    width={8}
    height={8}
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="4" cy="4" r="4" fill="#2D5A3B" fillOpacity="0.4" />
    <circle cx="4" cy="3.88867" r="2" fill="#2D5A3B" />
  </svg>
);

export interface ManagementCallStructuredCardProps {
  /** 设计稿顶部弱标签（可为 Earnings Call） */
  sectionLabel: string;
  title: string;
  description: string;
  asset: ChatStructuredAsset;
  /** 如 Management Call */
  callTypeLabel: string;
  insights: ChatStructuredInsightBullet[];
  className?: string;
}

/**
 * Management Call — 电话会要点列表（标题 + 解读行）。
 */
export const ManagementCallStructuredCard: React.FC<
  ManagementCallStructuredCardProps
> = ({
  sectionLabel,
  title,
  description,
  asset,
  callTypeLabel,
  insights,
  className,
}) => {
  const router = useShellRouter();
  return (
    <ChatStructuredCardShell className={className}>
      <ChatCardEyebrow text={sectionLabel} />
      <div className="flex flex-col gap-4">
        <ChatCardIntro title={title} description={description} />
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-1">
            <div
              className={cn(
                "relative size-5 shrink-0 overflow-hidden rounded-1.5 border border-border_solid",
                asset.url && "cursor-pointer",
              )}
              data-openreplay-label={
                asset.url
                  ? `ManagementCallStructuredCard · asset | ${asset.url}`
                  : `ManagementCallStructuredCard · asset | ${asset.symbol}`
              }
              onClick={() => asset.url && router.push(asset.url)}
            >
              <NextImage
                src={asset.imageUrl}
                alt={asset.symbol}
                width={20}
                height={20}
                className="size-full object-cover"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1 body_bold_m leading-5">
              <span className="text-content_primary">{asset.symbol}</span>
              <span className="body_m font-normal text-content_secondary">
                {callTypeLabel}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-3 rounded-2 bg-background_solid_secondary px-2 py-3">
            {insights.map((block, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <div className="relative size-2 shrink-0" aria-hidden>
                    <ManagementBulletIcon />
                  </div>
                  <p className="body_medium_m text-content_primary">
                    {block.title}
                  </p>
                </div>
                {block.lines.map((line, li) => (
                  <p key={li} className="body_m pl-3 text-content_tertiary">
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ChatStructuredCardShell>
  );
};
